import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { faMousePointer } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';

import type { Breadcrumb, FieldCollection } from '@feathr/blackbox';
import { hiddenFlavors } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';

import BreadcrumbDetails from '../BreadcrumbDetails';
import { flavorToColorMap, flavorToIconMap } from '../FlavorChip';
import BreadcrumbsTimelineElementLoader from './BreadcrumbsTimelineElementLoader';

import 'react-vertical-timeline-component/style.min.css';
import * as styles from './BreadcrumbsTimeline.css';

interface IProps {
  breadcrumbs: Breadcrumb[];
  columns?: 1 | 2;
  isLoading?: boolean;
  context: FieldCollection.Person | FieldCollection.Partner;
  hasViewRawData: boolean;
}

function BreadcrumbsTimeline({
  breadcrumbs,
  columns = 2,
  context,
  hasViewRawData,
  isLoading = false,
}: IProps): JSX.Element {
  // We need need to override default to use slate since we aren't applying a CSS style
  const flavorsOverride = {
    ...flavorToColorMap,
    default: 'slate',
  };

  return (
    <VerticalTimeline className={styles.root} layout={columns === 2 ? '2-columns' : '1-column'}>
      {breadcrumbs
        .filter((breadcrumb) => !hiddenFlavors.includes(breadcrumb.get('flvr')))
        .map((breadcrumb) => {
          const flavor = breadcrumb.get('flvr');
          const icon: IconDefinition = flavorToIconMap[flavor] || faMousePointer;
          const color = flavorsOverride[flavor] || flavorsOverride.default;

          return (
            <VerticalTimelineElement
              className={styles.item}
              icon={<FontAwesomeIcon icon={icon} />}
              iconClassName={styles.icon}
              iconStyle={{
                backgroundColor: cssVar(`--color-${color}-50`),
                color: cssVar(`--color-${color}-700`),
              }}
              key={breadcrumb.id}
            >
              <BreadcrumbDetails
                breadcrumb={breadcrumb}
                context={context}
                hasViewRawData={hasViewRawData}
              />
            </VerticalTimelineElement>
          );
        })}
      {isLoading && <BreadcrumbsTimelineElementLoader key={'loading'} />}
    </VerticalTimeline>
  );
}

export default observer(BreadcrumbsTimeline);
